import { useRouter } from "next/router";
import { useCurrentUser } from "queries/current-user";
import { isUUID } from "queries/lib/uuid";
import { useSyncExternalStore } from "react";
import { safeLocalStorage } from "ui/lib/safe-storage";
import { useBootcamps } from "ui/lib/useBootcamps";

const KEY = "apollo-last-team";

export type LastTeam = { id: string; name: string };
type Stored = LastTeam & { userId: string };

const listeners = new Set<() => void>();

// Read from storage once, then held here: each tab keeps its own team even
// when another tab writes a different one, and when storage is blocked.
let memory: Stored | null | undefined;

const parse = (raw: string | null): Stored | null => {
  try {
    const team = JSON.parse(raw || "null");
    return typeof team?.userId === "string" &&
      isUUID(team.id) &&
      typeof team.name === "string"
      ? { userId: team.userId, id: team.id, name: team.name }
      : null;
  } catch {
    return null;
  }
};

const readStored = (): Stored | null => {
  if (memory === undefined) memory = parse(safeLocalStorage.getItem(KEY));
  return memory;
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** The team the shell last showed this user, so other routes and the next visit keep it. */
export const rememberTeam = (team: LastTeam, userId?: string) => {
  if (!userId || !isUUID(team?.id) || !team.name) return;
  const current = readStored();
  if (
    current?.userId === userId &&
    current.id === team.id &&
    current.name === team.name
  )
    return;
  memory = { userId, id: team.id, name: team.name };
  safeLocalStorage.setItem(KEY, JSON.stringify(memory));
  listeners.forEach((listener) => listener());
};

/** Null on the server and during hydration, so both renders agree. */
export const useLastTeam = (): LastTeam | null => {
  const { currentUser } = useCurrentUser();
  const stored = useSyncExternalStore(subscribe, readStored, () => null);
  return stored && stored.userId === currentUser?.id ? stored : null;
};

/**
 * The team for links off a team route: the last team shown if the user still
 * has it, else their first. The stored team stands in only while the team
 * list loads.
 */
export const useFallbackTeamId = (): string | undefined => {
  const lastTeam = useLastTeam();
  const { bootcamps } = useBootcamps();
  if (!bootcamps?.length) return lastTeam?.id;
  return (
    bootcamps.find(({ id }) => id === lastTeam?.id)?.id ?? bootcamps[0]?.id
  );
};

/** The team the shell is about: the route's, or the fallback off team routes. */
export const useShellTeamId = (): string | undefined => {
  const { query } = useRouter();
  const fallback = useFallbackTeamId();
  return isUUID(query.bootcampId) ? (query.bootcampId as string) : fallback;
};

/** The shell team's calendar, or `/programs` (a server redirect) with no team yet. */
export const useHomeHref = () => {
  const teamId = useShellTeamId();
  return teamId ? `/programs/${teamId}/overview` : "/programs";
};
